/* ============================================================
   components.jsx — Topbar, Leftbar, HUD, LayersCard, Inspector
   ============================================================ */

import React, { useMemo } from 'react';
import * as turf from '@turf/turf';
import { Icon, NumberField, Segment, AngleViz, Toggle } from './ui-primitives.jsx';
import { PG } from './geometry.js';

// ----- Topbar -----
export function Topbar({
  scenarios,
  scenario,
  scenarioId,
  hasScenario,
  onSwitchScenario,
  onNewScenario,
  onChangeAddress,
  onDeleteScenario,
  onOpenSettings,
  onOpenExport,
  onOpenAutofill,
  onClearLayout,
  onImportJson,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) {
  const scList = Object.values(scenarios || {});
  const DOT_COLORS = ['#6b9a4a', '#5b8fb0', '#bea78e', '#a85a3c', '#9a8bff', '#d4a23a'];
  return (
    <div className="topbar">
      <div className="brand">
        <div className="brand-mark">R</div>
        <div>
          <div className="brand-name">Parkplatz-Editor</div>
        </div>
      </div>

      <div className="scenario-picker">
        {scList.length === 0 && (
          <span className="scenario-empty">Kein Standort — Adresse eingeben, um zu starten</span>
        )}
        {scList.map((sc, i) => (
          <button
            key={sc.id}
            className={`scenario-tab ${scenarioId === sc.id ? 'is-active' : ''}`}
            onClick={() => onSwitchScenario(sc.id)}
            title={sc.address || sc.name}
          >
            <span className="dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />
            <span className="scenario-name">{sc.name}</span>
          </button>
        ))}
        <button className="scenario-tab scenario-tab-new" onClick={onNewScenario} title="Neuen Standort per Adresse anlegen">
          <Icon name="plus" size={12} />
          Neuer Standort
        </button>
      </div>

      {hasScenario && (
        <button
          className="address-chip"
          onClick={onChangeAddress}
          title="Standort ändern"
        >
          <Icon name="map-pin" size={12} />
          <span className="address-chip-text">
            {scenario.address ? scenario.address : `${scenario.center[1].toFixed(4)}, ${scenario.center[0].toFixed(4)}`}
          </span>
          <Icon name="pencil" size={11} />
        </button>
      )}

      <div className="topbar-spacer" />

      <div className="topbar-actions">
        <button className="icon-btn" disabled={!canUndo} onClick={onUndo} title="Rückgängig (⌘Z)">
          <Icon name="undo-2" size={14} />
        </button>
        <button className="icon-btn" disabled={!canRedo} onClick={onRedo} title="Wiederholen (⇧⌘Z)">
          <Icon name="redo-2" size={14} />
        </button>
        <div style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 6px' }} />
        <button className="icon-btn" onClick={onOpenAutofill} disabled={!hasScenario} title="Auto-Befüllen">
          <Icon name="wand-sparkles" size={14} />
          Auto-Befüllen
        </button>
        <button
          className="icon-btn"
          onClick={onClearLayout}
          disabled={!hasScenario}
          title="Alle Reihen und Fahrgassen entfernen"
        >
          <Icon name="eraser" size={14} />
          Leeren
        </button>
        <button className="icon-btn" onClick={onImportJson} title="Szenario aus JSON importieren">
          <Icon name="file-up" size={14} />
          Import
        </button>
        <button className="icon-btn" onClick={onOpenSettings} title="Einstellungen">
          <Icon name="settings" size={14} />
        </button>
        <button className="icon-btn primary" onClick={onOpenExport} disabled={!hasScenario}>
          <Icon name="file-output" size={14} />
          Export
        </button>
      </div>
    </div>
  );
}

// ----- Welcome overlay: shown when no scenario has been picked yet -----
export function WelcomeOverlay({ onPickAddress }) {
  return (
    <div className="welcome-overlay">
      <div className="welcome-card">
        <div className="welcome-icon"><Icon name="map-pin" size={26} /></div>
        <h1 className="welcome-title">Wo soll der Parkplatz entstehen?</h1>
        <p className="welcome-desc">
          Gib eine Adresse, einen Straßennamen oder einen Ort ein. Auf dem Satellitenbild
          zeichnest Du anschließend Flächen, Reihen und Hindernisse — der Editor berechnet
          die maximale Anzahl an Parkplätzen.
        </p>
        <button className="btn is-primary welcome-cta" onClick={onPickAddress}>
          <Icon name="search" size={14} />
          Adresse eingeben
        </button>
        <div className="welcome-shortcuts">
          <span>oder direkt loslegen:</span>
          <span className="kbd">R</span> Reihe ·
          <span className="kbd">B</span> Fläche ·
          <span className="kbd">F</span> Fahrgasse
          <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>(nach Adresswahl)</span>
        </div>
      </div>
    </div>
  );
}


// ----- Leftbar (drawing tools) -----
const TOOLS = [
  { id: 'select',    icon: 'mouse-pointer-2', label: 'Auswählen',        key: 'V' },
  { id: 'pan',       icon: 'hand',            label: 'Karte verschieben', key: 'H' },
  null,
  { id: 'boundary',  icon: 'pentagon',        label: 'Fläche definieren', key: 'B', color: '#ffd166' },
  { id: 'row',       icon: 'rows-3',          label: 'Parkreihe',         key: 'R', color: '#6ea8ff' },
  { id: 'lane',      icon: 'route',           label: 'Fahrgasse',         key: 'F', color: '#9a8bff' },
  { id: 'obstacle',  icon: 'shapes',          label: 'Hindernis',         key: 'O', color: '#ff7a5c' },
  null,
  { id: 'measure',   icon: 'ruler',           label: 'Messen',            key: 'M' },
  { id: 'entry',     icon: 'log-in',          label: 'Ein-/Ausfahrt',     key: 'E' },
  { id: 'coords',    icon: 'crosshair',       label: 'Koordinaten ablesen', key: 'C' },
];

export function Leftbar({ tool, onSelectTool }) {
  return (
    <div className="leftbar">
      {TOOLS.map((t, i) =>
        t === null
          ? <div key={'sep' + i} className="tool-divider" />
          : (
            <button
              key={t.id}
              className={`tool-btn ${tool === t.id ? 'is-active' : ''}`}
              onClick={() => onSelectTool(t.id)}
              title={`${t.label} (${t.key})`}
              style={t.color && tool !== t.id ? { color: t.color } : undefined}
            >
              <Icon name={t.icon} size={18} />
              <span className="tool-key">{t.key}</span>
            </button>
          )
      )}
    </div>
  );
}

// ----- HUD: capacity counter (top right) -----
export function HUD({ counts, target, scenario, slotDim, blockedCount }) {
  const total = counts.total;
  const effective = total - blockedCount;
  const pct = target > 0 ? effective / target : 0;
  const status = effective >= target ? 'is-met' : (effective >= target * 0.92 ? 'is-short' : 'is-short');
  const isOver = effective > target * 1.05;
  const barClass = effective >= target ? 'is-over' : (effective >= target * 0.92 ? 'is-short' : '');
  const statusLabel = effective >= target ? 'Ziel erreicht' : `−${target - effective}`;
  const targetPct = Math.min(100, (target / Math.max(target * 1.1, total)) * 100);

  return (
    <div className="hud">
      <div className="hud-card hud-counter">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div>
            <div className="hud-eyebrow">Kapazität</div>
            <div className="hud-figure">
              <span className="hud-current">{effective}</span>
              {target > 0 && <span className="hud-target">{target}</span>}
            </div>
          </div>
          {target > 0 && (
            <div className={`hud-status ${barClass || status}`}>
              <span className="dot" />
              {statusLabel}
            </div>
          )}
        </div>
        {target > 0 && (
          <div className="hud-bar">
            <div className={`hud-bar-fill ${barClass}`} style={{ width: `${Math.min(100, pct * 100)}%` }} />
            <div className="hud-bar-target" style={{ left: `${targetPct}%` }} />
          </div>
        )}
        <div className="hud-meta">
          <div className="hud-meta-cell">
            <div className="hud-meta-label">Gesamt</div>
            <div className="hud-meta-value">{total}</div>
          </div>
          <div className="hud-meta-cell">
            <div className="hud-meta-label">Blockiert</div>
            <div className={`hud-meta-value ${blockedCount > 0 ? 'is-warn' : ''}`}>{blockedCount}</div>
          </div>
          <div className="hud-meta-cell">
            <div className="hud-meta-label">Reihen</div>
            <div className="hud-meta-value">{counts.rows}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- LayersCard: bottom-left layer toggles -----
const LAYER_DEFS = [
  { id: 'satellite', label: 'Satellit',    icon: 'image',     color: '#888' },
  { id: 'boundary',  label: 'Flächen',     icon: 'pentagon',  color: '#ffd166' },
  { id: 'slots',     label: 'Parklücken',  icon: 'rows-3',    color: '#bea78e' },
  { id: 'lanes',     label: 'Fahrgassen',  icon: 'route',     color: '#9a8bff' },
  { id: 'obstacles', label: 'Hindernisse', icon: 'shapes',    color: '#ff7a5c' },
  { id: 'labels',    label: 'Beschriftung',icon: 'type',      color: '#bea78e' },
  { id: 'entries',   label: 'Ein-/Ausfahrt',icon: 'log-in',   color: '#6b9a4a' },
  { id: 'coords',    label: 'Koordinaten', icon: 'crosshair', color: '#a4a09a' },
];

export function LayersCard({ layers, onToggle }) {
  return (
    <div className="layers-card">
      <div className="layers-card-header">
        <Icon name="layers" size={11} />
        Ebenen
      </div>
      {LAYER_DEFS.map((l) => {
        const visible = layers[l.id];
        return (
          <div
            key={l.id}
            className={`layer-row ${visible ? '' : 'is-hidden'}`}
            onClick={() => onToggle(l.id)}
          >
            <span className="layer-dot" style={{
              color: l.color,
              background: visible ? l.color : 'transparent'
            }} />
            <span className="layer-label">{l.label}</span>
            <Icon name={visible ? 'eye' : 'eye-off'} size={12} />
          </div>
        );
      })}
    </div>
  );
}

// ----- StatusBar (bottom strip) -----
export function StatusBar({ tool, mouseCoord, mapZoom, scenario, drawing }) {
  const toolDef = TOOLS.find(t => t && t.id === tool) || { label: '—' };
  return (
    <div className="statusbar">
      <span>Werkzeug: <strong style={{ color: 'var(--text)' }}>{toolDef.label}</strong></span>
      <span className="sep" />
      {drawing && (
        <>
          <span style={{ color: 'var(--sand)' }}>
            ↳ {drawing.type === 'boundary' ? 'Fläche' : drawing.type === 'row' ? 'Reihe' : drawing.type === 'lane' ? 'Gasse' : 'Hindernis'} zeichnen · {drawing.points.length} Punkt(e) gesetzt
          </span>
          <span className="sep" />
        </>
      )}
      <span>Szenario: <strong style={{ color: 'var(--text)' }}>{scenario.name}</strong></span>
      <span className="sep" />
      <span className="mono">
        {mouseCoord ? `${mouseCoord.lat.toFixed(5)}° N, ${mouseCoord.lng.toFixed(5)}° E` : '—'}
      </span>
      <span className="sep" />
      <span className="mono">Zoom {mapZoom}</span>
      <div style={{ flex: 1 }} />
      <span style={{ color: 'var(--text-3)' }}>
        Auto-Speichern · zuletzt vor wenigen Sekunden
      </span>
    </div>
  );
}

// ----- DrawHint (above-map ribbon when in drawing tool) -----
export function DrawHint({ tool, drawing, onFinish, onCancel }) {
  if (!tool || tool === 'select' || tool === 'pan') return null;

  const isPolygonTool = tool === 'boundary' || tool === 'obstacle';
  const isLineTool = tool === 'row' || tool === 'lane' || tool === 'measure';
  const isPointTool = tool === 'entry';

  let label, helper;
  if (isPolygonTool) {
    label = tool === 'boundary' ? 'Fläche zeichnen' : 'Hindernis zeichnen';
    if (!drawing || drawing.points.length === 0) {
      helper = 'Klicke, um den ersten Punkt zu setzen';
    } else if (drawing.points.length < 3) {
      helper = `${drawing.points.length} Punkt(e) gesetzt — mindestens 3 nötig`;
    } else {
      helper = 'Doppelklick zum Schließen';
    }
  } else if (isLineTool) {
    if (tool === 'measure') label = 'Strecke messen';
    else label = tool === 'row' ? 'Parkreihe zeichnen' : 'Fahrgasse zeichnen';
    if (!drawing || drawing.points.length === 0) {
      helper = 'Setze den Startpunkt';
    } else {
      helper = 'Setze den Endpunkt';
    }
  } else if (isPointTool) {
    label = 'Ein-/Ausfahrt platzieren';
    helper = 'Klicke auf die gewünschte Position';
  }

  return (
    <div className="draw-hint">
      <Icon name="crosshair" size={14} />
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{ color: 'var(--text-2)' }}>· {helper}</span>
      {drawing && drawing.points.length >= (isPolygonTool ? 3 : 1) && isPolygonTool && (
        <button className="btn is-primary" style={{ height: 22, padding: '0 8px', fontSize: 11, marginLeft: 6 }} onClick={onFinish}>
          Abschließen <span className="kbd">↵</span>
        </button>
      )}
      <button className="btn" style={{ height: 22, padding: '0 8px', fontSize: 11 }} onClick={onCancel}>
        Abbrechen <span className="kbd">esc</span>
      </button>
    </div>
  );
}

// ----- Compass + scalebar in corner -----
export function Compass() {
  return (
    <div className="compass" title="Norden ist oben">
      <svg viewBox="0 0 32 32" width="32" height="32" style={{ position: 'absolute', inset: 6 }}>
        <polygon points="16,4 20,16 16,12 12,16" fill="var(--sand)" />
        <polygon points="16,28 20,16 16,20 12,16" fill="var(--text-3)" />
      </svg>
      <span style={{ position: 'absolute', top: 2, color: 'var(--text)', fontSize: 9, fontFamily: 'var(--font-mono)' }}>N</span>
    </div>
  );
}

// ============================================================
// INSPECTOR — context-aware right panel
// ============================================================

export function Inspector({ selection, scenario, onUpdateRow, onDeleteObject, onAddRowToBoundary }) {
  if (!selection) return <EmptyInspector />;

  if (selection.type === 'row') {
    const row = scenario.rows.find(r => r.id === selection.id);
    if (!row) return <EmptyInspector />;
    return <RowInspector row={row} onUpdate={(patch) => onUpdateRow(row.id, patch)} onDelete={() => onDeleteObject('row', row.id)} />;
  }

  if (selection.type === 'lane') {
    const lane = scenario.lanes.find(l => l.id === selection.id);
    if (!lane) return <EmptyInspector />;
    return <LaneInspector lane={lane} onUpdate={(patch) => onUpdateRow(lane.id, patch, 'lane')} onDelete={() => onDeleteObject('lane', lane.id)} />;
  }

  if (selection.type === 'boundary') {
    const b = scenario.boundaries.find(x => x.id === selection.id);
    if (!b) return <EmptyInspector />;
    return <BoundaryInspector boundary={b} onAutoFill={() => onAddRowToBoundary(b.id)} onDelete={() => onDeleteObject('boundary', b.id)} />;
  }

  if (selection.type === 'obstacle') {
    const o = scenario.obstacles.find(x => x.id === selection.id);
    if (!o) return <EmptyInspector />;
    return <ObstacleInspector obstacle={o} onDelete={() => onDeleteObject('obstacle', o.id)} />;
  }

  return <EmptyInspector />;
}

function EmptyInspector() {
  return (
    <div className="inspector">
      <div className="inspector-header">
        <div className="inspector-title">
          <Icon name="info" size={14} />
          Inspektor
        </div>
      </div>
      <div className="inspector-body">
        <div className="insp-empty">
          <Icon name="mouse-pointer-click" size={28} />
          <h3>Nichts ausgewählt</h3>
          <p>Wähle ein Objekt auf der Karte aus, um Eigenschaften zu bearbeiten.</p>
          <div className="hint-keys">
            <div className="hint-row"><span className="kbd">R</span> Parkreihe zeichnen</div>
            <div className="hint-row"><span className="kbd">B</span> Fläche definieren</div>
            <div className="hint-row"><span className="kbd">F</span> Fahrgasse setzen</div>
            <div className="hint-row"><span className="kbd">O</span> Hindernis markieren</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RowInspector({ row, onUpdate, onDelete }) {
  const lengthM = useMemo(() => PG.distanceOf(row.start, row.end), [row.start, row.end]);
  const recLane = PG.recommendedLaneWidth(row.angle);
  const slotCount = useMemo(() => {
    const step = PG.step(row.angle, row.slotWidth, row.slotLength);
    return Math.floor(lengthM / step) * (row.side === 'both' ? 2 : 1);
  }, [lengthM, row.angle, row.slotWidth, row.slotLength, row.side]);

  return (
    <div className="inspector">
      <div className="inspector-header">
        <div className="inspector-title">
          <Icon name="rows-3" size={14} />
          Parkreihe
          <span className="inspector-subtitle">{row.label}</span>
        </div>
      </div>
      <div className="inspector-body">

        {/* ---- Geometrie summary ---- */}
        <div className="insp-section">
          <div className="insp-section-title">
            <span>Geometrie</span>
            <span className="mono" style={{ color: 'var(--sand)' }}>{slotCount} Slots</span>
          </div>
          <div className="insp-row">
            <span className="insp-label">Länge</span>
            <span className="insp-value insp-num">{lengthM.toFixed(1)} m</span>
          </div>
          <div className="insp-row">
            <span className="insp-label">Slot-Maße</span>
            <span className="insp-value insp-num">{row.slotWidth.toFixed(1)} × {row.slotLength.toFixed(1)} m</span>
          </div>
        </div>

        {/* ---- Angle ---- */}
        <div className="insp-section">
          <div className="insp-section-title">
            <span>Winkel · {angleNameDe(row.angle)}</span>
            <span className="mono" style={{ color: 'var(--sand)' }}>{Math.round(row.angle)}°</span>
          </div>

          <AngleViz
            angle={row.angle}
            slantSign={row.slantSign}
            side={row.side === 'both' ? 'right' : row.side}
            doubleSided={row.side === 'both'}
            slotWidth={row.slotWidth}
            slotLength={row.slotLength}
          />

          <div className="angle-track">
            <input
              type="range"
              className="angle-slider"
              min={0}
              max={90}
              step={1}
              value={row.angle}
              onChange={(e) => onUpdate({ angle: parseInt(e.target.value) })}
            />
          </div>

          <div className="angle-presets">
            {[0, 30, 45, 60, 75, 90].map(a => (
              <button
                key={a}
                className={`angle-preset ${row.angle === a ? 'is-active' : ''}`}
                onClick={() => onUpdate({ angle: a })}
              >
                {a}°
              </button>
            ))}
          </div>

          {row.angle > 0.5 && row.angle < 89.5 && (
            <div className="insp-row" style={{ marginTop: 10 }}>
              <span className="insp-label">Neigung</span>
              <Segment
                value={row.slantSign}
                options={[
                  { value:  1, label: '↗ vorw.' },
                  { value: -1, label: '↖ rückw.' },
                ]}
                onChange={(v) => onUpdate({ slantSign: v })}
              />
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <div className={`callout ${row.angle >= 80 ? '' : 'is-info'}`}>
              <Icon name={row.angle >= 80 ? 'alert-triangle' : 'info'} size={13} />
              <div>
                Empfohlene Fahrgassen-Breite ≥ <strong>{recLane.toFixed(1)} m</strong> für {Math.round(row.angle)}°{' '}
                {row.angle <= 0.5 ? 'Längsparken'
                  : row.angle === 45 ? '· Fischgräten'
                  : row.angle >= 80 ? '· Senkrechtparken'
                  : '· Schrägparken'}.
              </div>
            </div>
          </div>
        </div>

        {/* ---- Sides / orientation ---- */}
        <div className="insp-section">
          <div className="insp-section-title">Aufstellung</div>

          <div className="insp-row">
            <span className="insp-label">Seiten</span>
            <Segment
              value={row.side}
              options={[
                { value: 'left',  label: 'Links' },
                { value: 'right', label: 'Rechts' },
                { value: 'both',  label: 'Beidseitig' },
              ]}
              onChange={(v) => onUpdate({ side: v })}
            />
          </div>

          <div className="insp-row">
            <span className="insp-label">Einparken</span>
            <Segment
              value={row.facing}
              options={[
                { value: 'in',  label: 'Vorwärts' },
                { value: 'out', label: 'Rückwärts' },
              ]}
              onChange={(v) => onUpdate({ facing: v })}
            />
          </div>

          <div className="insp-row" style={{ marginTop: 4 }}>
            <Toggle
              label="Behindertenparkplätze automatisch"
              hint="Jede 8. Lücke wird 3,5 m breit, in Eingangsnähe"
              checked={row.accessibleEvery > 0}
              onChange={(c) => onUpdate({ accessibleEvery: c ? 8 : 0 })}
            />
          </div>
        </div>

        {/* ---- Slot dimensions ---- */}
        <div className="insp-section">
          <div className="insp-section-title">Slot-Maße</div>

          <div className="insp-row">
            <span className="insp-label">Breite</span>
            <NumberField
              value={row.slotWidth}
              onChange={(v) => onUpdate({ slotWidth: clamp(v, 2.0, 4.0) })}
              min={2.0} max={4.0} step={0.1}
            />
          </div>
          <div className="insp-row">
            <span className="insp-label">Länge</span>
            <NumberField
              value={row.slotLength}
              onChange={(v) => onUpdate({ slotLength: clamp(v, 3.5, 7.5) })}
              min={3.5} max={7.5} step={0.1}
            />
          </div>
          <div className="callout is-info" style={{ marginTop: 8 }}>
            <Icon name="lightbulb" size={13} />
            <div>
              EU-Standard 2,5 × 5,0 m. Für sehr breite Fahrzeuge auf 2,7 × 5,2 m erhöhen.
            </div>
          </div>
        </div>

        <div className="insp-section">
          <div className="insp-actions">
            <button className="btn is-danger" onClick={onDelete}>
              <Icon name="trash-2" size={13} />
              Reihe löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LaneInspector({ lane, onUpdate, onDelete }) {
  const lengthM = useMemo(() => PG.distanceOf(lane.start, lane.end), [lane.start, lane.end]);
  return (
    <div className="inspector">
      <div className="inspector-header">
        <div className="inspector-title">
          <Icon name="route" size={14} />
          Fahrgasse
        </div>
      </div>
      <div className="inspector-body">
        <div className="insp-section">
          <div className="insp-row">
            <span className="insp-label">Länge</span>
            <span className="insp-value insp-num">{lengthM.toFixed(1)} m</span>
          </div>
          <div className="insp-row">
            <span className="insp-label">Breite</span>
            <NumberField
              value={lane.width}
              onChange={(v) => onUpdate({ width: clamp(v, 2.5, 12.0) })}
              min={2.5} max={12.0} step={0.1}
            />
          </div>
          <div className="insp-row">
            <span className="insp-label">Richtung</span>
            <Segment
              value={lane.direction || 'two-way'}
              options={[
                { value: 'one-way', label: 'Einbahn' },
                { value: 'two-way', label: 'Beide' },
              ]}
              onChange={(v) => onUpdate({ direction: v })}
            />
          </div>
        </div>
        <div className="insp-section">
          <div className="insp-actions">
            <button className="btn is-danger" onClick={onDelete}>
              <Icon name="trash-2" size={13} /> Gasse löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BoundaryInspector({ boundary, onAutoFill, onDelete }) {
  const turfPoly = turf.polygon([boundary.polygon]);
  const area = turf.area(turfPoly);
  return (
    <div className="inspector">
      <div className="inspector-header">
        <div className="inspector-title">
          <Icon name="pentagon" size={14} />
          Fläche
          <span className="inspector-subtitle">{boundary.label || '—'}</span>
        </div>
      </div>
      <div className="inspector-body">
        <div className="insp-section">
          <div className="insp-row">
            <span className="insp-label">Fläche</span>
            <span className="insp-value insp-num">
              {(area).toFixed(0)} m² · {(area/10000).toFixed(2)} ha
            </span>
          </div>
          <div className="insp-row">
            <span className="insp-label">Ecken</span>
            <span className="insp-value insp-num">{boundary.polygon.length - 1}</span>
          </div>
        </div>

        <div className="insp-section">
          <div className="insp-section-title">Aktionen</div>
          <button className="btn is-primary" style={{ width: '100%', marginBottom: 6 }} onClick={onAutoFill}>
            <Icon name="wand-sparkles" size={13} />
            Mit dichtester Aufteilung füllen
          </button>
          <div className="callout is-info" style={{ marginTop: 6 }}>
            <Icon name="info" size={13} />
            <div>
              Wähle anschließend Winkel und Slot-Maße, der Editor berechnet die maximale Kapazität.
            </div>
          </div>
        </div>

        <div className="insp-section">
          <div className="insp-actions">
            <button className="btn is-danger" onClick={onDelete}>
              <Icon name="trash-2" size={13} /> Fläche löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ObstacleInspector({ obstacle, onDelete }) {
  return (
    <div className="inspector">
      <div className="inspector-header">
        <div className="inspector-title">
          <Icon name="shapes" size={14} />
          Hindernis
          <span className="inspector-subtitle">{obstacle.label || obstacle.kind || '—'}</span>
        </div>
      </div>
      <div className="inspector-body">
        <div className="insp-section">
          <div className="callout">
            <Icon name="alert-triangle" size={13} />
            <div>
              Parklücken, die mit diesem Hindernis kollidieren, werden automatisch als&nbsp;
              <strong>blockiert</strong> markiert und nicht zur Kapazität gezählt.
            </div>
          </div>
        </div>
        <div className="insp-section">
          <div className="insp-actions">
            <button className="btn is-danger" onClick={onDelete}>
              <Icon name="trash-2" size={13} /> Hindernis löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- helpers -----
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function angleNameDe(a) {
  if (a <= 0.5)  return 'Längsparken';
  if (a <= 32)   return 'Flach';
  if (a <= 50)   return 'Fischgräten';
  if (a <= 68)   return 'Schrägparken';
  if (a <= 80)   return 'Steil';
  return 'Senkrechtparken';
}

